import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  computed,
  effect,
  inject,
  input,
  output,
  signal,
  untracked,
} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { FormBuilder, ReactiveFormsModule } from '@angular/forms';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';
import { accessDenialMessage } from '../../utils/access-error.utils';
import { MatIconModule } from '@angular/material/icon';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { DatePickerDirective, SpinDirective } from '../../shared/directives';
import { ModalComponent, ModalConfig } from '../modal/modal';
import { ModalStatusComponent } from '../modal-status/modal-status';
import { HuxleyComponent } from '../huxley/huxley';
import { SectionIconComponent, SectionIconKey } from '../section-icon/section-icon';
import { EntityIconComponent, EntityIconInput } from '../entity-icon/entity-icon';
import { getDocKindFromSubtypeCode } from '../../utils/document.utils';
import { getControlType } from '../../utils/control.utils';
import { StepIndicatorComponent, StepConfig } from '../step-indicator/step-indicator';
import { ToolbarSearchComponent } from '../toolbar-search/toolbar-search';
import {
  AssessmentRecord,
  AssessmentTypeEnum,
  ControlRecord,
  CreateEntityCategory,
  DOCUMENT_CONTENT_TYPES,
  FrameworkRecord,
  FRAMEWORK_METADATA,
  FrameworkType,
  Task,
  TaskCreateRequest,
  TaskType,
  type EntityLanguage,
} from '../../models';
import { AssigneePickerComponent } from '../assignee-picker/assignee-picker';
import { LanguagePickerComponent } from '../language-picker/language-picker';
import { LanguageBadgeComponent } from '../language-badge/language-badge';
import { StateStoreService } from '../../services/state-store/state-store';
import { TaskApi } from '../../services/task-api/task-api';
import { OrgManagementApi } from '../../services/org-management-api/org-management-api';
import { getEntityLanguageLabel } from '../../utils/language.utils';
type ModalStep = 'link' | 'language' | 'details' | 'review';

const TASK_TYPES_REQUIRING_LINK = new Set([TaskType.REVIEW, TaskType.CREATE]);
type LinkKind = 'document' | 'control' | 'framework' | 'vendor' | 'assessment' | 'none';

interface TaskTypeOption {
  value: TaskType;
  label: string;
}

interface LinkOption {
  value: LinkKind;
  label: string;
  /** Section-icon key for the five entity options — same SVGs the sidebar
   *  renders, so the link card reads as "this entity, the one I see in the
   *  nav". `null` for the `none` ("skip linking") affordance, which renders
   *  a Material `link_off` glyph instead. */
  sectionIcon: SectionIconKey | null;
}

interface EntityItem {
  id: string;
  name: string;
  subtitle?: string;
  iconInput: EntityIconInput;
}

@Component({
  selector: 'app-create-task-modal',
  templateUrl: './create-task-modal.html',
  styleUrl: './create-task-modal.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ReactiveFormsModule,
    TranslocoModule,
    MatIconModule,
    MatSlideToggleModule,
    DatePickerDirective,
    SpinDirective,
    ModalComponent,
    ModalStatusComponent,
    HuxleyComponent,
    SectionIconComponent,
    EntityIconComponent,
    StepIndicatorComponent,
    ToolbarSearchComponent,
    AssigneePickerComponent,
    LanguagePickerComponent,
    LanguageBadgeComponent,
  ],
})
export class CreateTaskModal {
  private readonly fb = inject(FormBuilder);
  private readonly stateStore = inject(StateStoreService);
  private readonly taskApi = inject(TaskApi);
  private readonly api = inject(OrgManagementApi);
  private readonly destroyRef = inject(DestroyRef);
  private readonly transloco = inject(TranslocoService);
  private readonly translationVersion = signal(0);
  readonly contentLanguage = signal<EntityLanguage>(this.stateStore.orgDefaultContentLanguage());

  readonly isVisible = input(false);
  readonly orgId = input<string | null>(null);
  /** Optional pre-selection for the "linked entity" step, driven by an
   *  upstream flow that already knows the shape (currently the DPIA
   *  measure → "Materialise task" chain sets `linkKind='control'` +
   *  `entityId=measure.control_id`). Both must be provided together;
   *  either null → the step opens with no pre-selection. Applied once
   *  per visibility flip and reset on close. */
  readonly initialLinkKind = input<LinkKind | null>(null);
  readonly initialEntityId = input<string | null>(null);

  readonly taskCreated = output<Task>();
  readonly cancelled = output<void>();

  /** Guards the one-shot pre-selection — flips true on first apply,
   *  resets on modal close. Prevents parent re-renders from clobbering
   *  user overrides within the same open session. */
  private readonly _hasPatchedFromPreselect = signal(false);

  readonly steps = computed<StepConfig[]>(() => {
    this.translationVersion();
    const t = (k: string) => this.transloco.translate(`modals.create_task.${k}`);
    // Link chooses WHAT the task attaches to; Language locks how AI
    // content is authored + which template variant loads for the
    // details step. Language sits between them so choosing a linked
    // entity picks a template AFTER the language is set.
    return [
      { key: 'link', label: t('step_link'), detail: this.selectedEntityName() || undefined },
      {
        key: 'language',
        label: this.transloco.translate('language_step.step_label'),
        detail: getEntityLanguageLabel(this.contentLanguage()),
      },
      { key: 'details', label: t('step_details') },
      { key: 'review', label: t('step_review') },
    ];
  });

  readonly taskTypes = computed<TaskTypeOption[]>(() => {
    this.translationVersion();
    const t = (k: string) => this.transloco.translate(`modals.create_task.${k}`);
    return [
      { value: TaskType.UPLOAD, label: t('task_type_upload') },
      { value: TaskType.REVIEW, label: t('task_type_review') },
      { value: TaskType.CREATE, label: t('task_type_create') },
      { value: TaskType.ASSESS, label: t('task_type_assess') },
      { value: TaskType.OTHER, label: t('task_type_other') },
    ];
  });

  readonly linkOptions = computed<LinkOption[]>(() => {
    this.translationVersion();
    const t = (k: string) => this.transloco.translate(`modals.create_task.${k}`);
    return [
      { value: 'document', label: t('link_kind_document'), sectionIcon: 'documents' },
      { value: 'control', label: t('link_kind_control'), sectionIcon: 'controls' },
      { value: 'framework', label: t('link_kind_framework'), sectionIcon: 'frameworks' },
      { value: 'vendor', label: t('link_kind_vendor'), sectionIcon: 'vendors' },
      { value: 'assessment', label: t('link_kind_assessment'), sectionIcon: 'assessment' },
      { value: 'none', label: t('link_kind_none'), sectionIcon: null },
    ];
  });

  private readonly _currentStep = signal<ModalStep>('link');
  readonly currentStep = this._currentStep.asReadonly();

  /**
   * Highest step ever reached this session. Advances only on forward
   * navigation; stays put when the user jumps backwards via the Back
   * button or the timeline. Reset on modal open/close so the next
   * launch starts fresh at Link.
   */
  private readonly _maxReachedStep = signal<ModalStep>('link');
  readonly maxReachedStep = this._maxReachedStep.asReadonly();

  /** Forward-navigation helper — sets current AND advances max when needed. */
  private setStep(next: ModalStep): void {
    this._currentStep.set(next);
    const list = this.steps();
    const nextIdx = list.findIndex(s => s.key === next);
    const maxIdx = list.findIndex(s => s.key === this._maxReachedStep());
    if (nextIdx > maxIdx) {
      this._maxReachedStep.set(next);
    }
  }

  /**
   * Timeline click — user asked to jump to a previously-visited step.
   * The step-indicator has already gated to visited-and-not-current;
   * defense-in-depth verify here too. No side effects: entity picks,
   * assignees, dates, content-language all persist so revisiting a
   * step surfaces the previously-captured data untouched.
   */
  protected onStepClicked(key: string): void {
    if (this.isTerminalState()) return;
    const list = this.steps();
    const clickedIdx = list.findIndex(s => s.key === key);
    const maxIdx = list.findIndex(s => s.key === this._maxReachedStep());
    if (clickedIdx < 0 || clickedIdx > maxIdx) return;
    this._error.set(null);
    this._errorIsDenial.set(false);
    this._currentStep.set(key as ModalStep);
  }

  readonly selectedTaskType = signal<TaskType | null>(null);
  readonly selectedLink = signal<LinkKind | null>(null);
  readonly selectedEntityId = signal<string | null>(null);
  readonly selectedEntitySubtype = signal<string | null>(null);
  readonly entitySearchQuery = signal('');

  readonly isCreateTask = computed(() => this.selectedTaskType() === TaskType.CREATE);

  /** Template-side binding — the CREATE-task subtype picker builds an
   *  `EntityIconInput` inline from a subtype code. */
  protected readonly getDocKindFromSubtypeCode = getDocKindFromSubtypeCode;

  readonly documentSubtypeOptions = computed(() => {
    this.translationVersion();
    return DOCUMENT_CONTENT_TYPES.map(dt => ({
      value: dt.value as string,
      label: dt.label,
      description: this.transloco.translate<string>(dt.descriptionKey),
    }));
  });

  readonly frameworkSubtypeOptions = (Object.keys(FRAMEWORK_METADATA) as FrameworkType[]).map(
    k => ({
      value: k as string,
      label: FRAMEWORK_METADATA[k].defaultName,
      description: FRAMEWORK_METADATA[k].defaultDescription,
    })
  );

  readonly createEntitySubtypeOptions = computed<
    { value: string; label: string; description: string }[]
  >(() => {
    const link = this.selectedLink();
    if (link === 'document') return this.documentSubtypeOptions();
    if (link === 'framework') return this.frameworkSubtypeOptions;
    return [];
  });

  private readonly _controls = signal<ControlRecord[]>([]);
  private readonly _frameworks = signal<FrameworkRecord[]>([]);
  private readonly _assessments = signal<AssessmentRecord[]>([]);
  private readonly _controlsLoading = signal(false);
  private readonly _frameworksLoading = signal(false);
  private readonly _assessmentsLoading = signal(false);

  readonly isEntityLoading = computed(() => {
    const kind = this.selectedLink();
    if (kind === 'document') return this.stateStore.documentsLoading();
    if (kind === 'vendor') return this.stateStore.vendorsLoading();
    if (kind === 'control') return this._controlsLoading();
    if (kind === 'framework') return this._frameworksLoading();
    if (kind === 'assessment') return this._assessmentsLoading();
    return false;
  });

  private readonly _isLoading = signal(false);
  private readonly _error = signal<string | null>(null);
  /** True when {@link error} is a permission answer rather than a failure —
   *  swaps the red-error banner for the amber-lock one. */
  private readonly _errorIsDenial = signal(false);
  readonly errorIsDenial = this._errorIsDenial.asReadonly();
  private readonly _successMessage = signal<string | null>(null);
  private _successTimer: ReturnType<typeof setTimeout> | null = null;

  readonly isLoading = this._isLoading.asReadonly();
  readonly error = this._error.asReadonly();
  readonly successMessage = this._successMessage.asReadonly();

  readonly isTerminalState = computed(() => this._isLoading() || !!this._successMessage());

  readonly requiresLink = computed(() => TASK_TYPES_REQUIRING_LINK.has(this.selectedTaskType()!));

  /**
   * Link-card view models. We render every option for *every* task type so
   * the grid's card count — and therefore the modal's height — never
   * changes when the user switches task type. Incompatible options are
   * `disabled` with an explanatory `disabledReason` instead of being
   * filtered out. Today the only incompatibility is `none` + a task type
   * that requires a link (REVIEW / CREATE); adding more rules is a matter
   * of extending this single computed.
   */
  readonly linkOptionViews = computed<
    (LinkOption & { disabled: boolean; disabledReason: string | null })[]
  >(() => {
    this.translationVersion();
    const required = this.requiresLink();
    const typeLabel = this.taskTypeLabel(this.selectedTaskType());
    return this.linkOptions().map(opt => {
      if (opt.value === 'none' && required) {
        return {
          ...opt,
          disabled: true,
          disabledReason: this.transloco.translate(
            'modals.create_task.disabled_reason_requires_link',
            { type: typeLabel }
          ),
        };
      }
      return { ...opt, disabled: false, disabledReason: null };
    });
  });

  readonly hasValidLink = computed(() => {
    const link = this.selectedLink();
    if (!link || link === 'none') return false;
    if (this.isCreateTask()) {
      if (link === 'control' || link === 'vendor') return true;
      return !!this.selectedEntitySubtype();
    }
    return !!this.selectedEntityId();
  });

  readonly form = this.fb.nonNullable.group({
    name: [''],
    description: [''],
    start_date: [''],
    due_date: [''],
    needs_approval: [false],
    recurring: [{ value: false, disabled: true }],
  });

  readonly entityItems = computed<EntityItem[]>(() => {
    const kind = this.selectedLink();
    switch (kind) {
      case 'document':
        return this.stateStore.documents().map(d => ({
          id: d.document_id,
          name: d.name,
          subtitle: d.type,
          iconInput: { kind: 'document', docKind: getDocKindFromSubtypeCode(d.type) },
        }));
      case 'control':
        return this._controls().map(c => ({
          id: c.control_id,
          name: c.name,
          subtitle: c.implementation_status,
          iconInput: { kind: 'control', controlType: getControlType(c) },
        }));
      case 'framework':
        return this._frameworks().map(f => ({
          id: f.framework_id,
          name: f.name,
          subtitle: f.framework_type,
          iconInput: { kind: 'framework', frameworkType: f.framework_type },
        }));
      case 'vendor':
        return this.stateStore.vendors().map(v => ({
          id: v.vendor_id,
          // Vendor name lives on the global registry record; the picker is
          // best-effort here. UX can hydrate from the registry list when
          // available.
          name: v.vendor_id.replace(/^VENDOR#/, ''),
          subtitle: v.privacy_role,
          iconInput: { kind: 'vendor' },
        }));
      case 'assessment':
        return this._assessments().map(a => ({
          id: a.assessment_id,
          name: a.assessment_name,
          subtitle: a.assessment_type,
          iconInput: { kind: 'assessment', assessmentType: a.assessment_type },
        }));
      default:
        return [];
    }
  });

  readonly filteredEntityItems = computed<EntityItem[]>(() => {
    const q = this.entitySearchQuery().trim().toLowerCase();
    if (!q) return this.entityItems();
    return this.entityItems().filter(
      item => item.name.toLowerCase().includes(q) || (item.subtitle ?? '').toLowerCase().includes(q)
    );
  });

  readonly selectedEntityName = computed<string>(() => {
    if (this.isCreateTask()) {
      const subtype = this.selectedEntitySubtype();
      if (!subtype) return '';
      return (
        this.documentSubtypeOptions().find(d => d.value === subtype)?.label ??
        this.frameworkSubtypeOptions.find(f => f.value === subtype)?.label ??
        subtype
      );
    }
    const id = this.selectedEntityId();
    if (!id) return '';
    return this.entityItems().find(e => e.id === id)?.name ?? '';
  });

  readonly todayDate = new Date().toISOString().split('T')[0];

  // ── Assignees ──
  readonly selectedAssignees = signal<string[]>([]);
  protected readonly orgMembers = this.stateStore.orgMembers;

  readonly selectedControl = computed<ControlRecord | null>(() => {
    if (this.selectedLink() !== 'control') return null;
    const entityId = this.selectedEntityId();
    if (!entityId) return null;
    return this._controls().find(c => c.control_id === entityId) ?? null;
  });

  readonly isLinkedControlRecurrent = computed(() => !!this.selectedControl()?.recurrence_unit);

  readonly recurringValue = toSignal(this.form.controls.recurring.valueChanges, {
    initialValue: false,
  });

  private readonly nameValue = toSignal(this.form.controls.name.valueChanges, { initialValue: '' });
  private readonly formValue = toSignal(this.form.valueChanges);

  readonly hasUnsavedWork = computed(() => {
    this.formValue();
    const step = this._currentStep();
    return (
      this.form.dirty ||
      step !== 'link' ||
      this.selectedLink() !== null ||
      this.selectedTaskType() !== null
    );
  });

  readonly modalConfig = computed<ModalConfig>(() => {
    this.translationVersion();
    const step = this._currentStep();
    const terminal = this.isTerminalState();
    const t = (k: string) => this.transloco.translate(`modals.create_task.${k}`);
    const base = {
      title: t('modal_title'),
      size: 'medium' as const,
      showCloseButton: !terminal,
      showFooter: !terminal,
      showLogo: true,
      showManagedCustomerContext: !terminal,
    };

    if (step === 'link') {
      return {
        ...base,
        primaryButtonText: t('primary_next'),
        secondaryButtonText: t('secondary_cancel'),
        primaryButtonDisabled: this.requiresLink() && !this.hasValidLink(),
      };
    }

    if (step === 'language') {
      // `contentLanguage` is signal-initialised to the org default and
      // never nullable, so Next is always enabled.
      return {
        ...base,
        primaryButtonText: t('primary_next'),
        secondaryButtonText: t('secondary_back'),
      };
    }

    if (step === 'details') {
      return {
        ...base,
        primaryButtonText: t('primary_next'),
        secondaryButtonText: t('secondary_back'),
        primaryButtonDisabled: !this.nameValue()?.trim(),
      };
    }

    const loading = this._isLoading();
    return {
      ...base,
      primaryButtonText: loading ? t('primary_creating') : t('primary_create'),
      secondaryButtonText: t('secondary_back'),
      primaryButtonDisabled: loading,
      primaryButtonLoading: loading,
    };
  });

  constructor() {
    this.transloco.langChanges$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.translationVersion.update(v => v + 1));

    effect(() => {
      if (!this.isVisible()) {
        this.resetModalState();
      } else {
        this._currentStep.set('link');
        this._maxReachedStep.set('link');
      }
    });

    // Default assignees from the linked control; clear when switching away
    effect(() => {
      const control = this.selectedControl();
      if (control?.assignees?.length) {
        this.selectedAssignees.set([...control.assignees]);
      } else {
        this.selectedAssignees.set([]);
      }
    });

    // One-shot pre-selection patch — applied on the first visibility flip
    // and latched via `_hasPatchedFromPreselect`. Read the inputs at
    // effect scope so late-arriving prefills still patch; write inside
    // `untracked` so we don't self-trigger. `loadEntitiesForKind` runs
    // as usual — the entity picker's list load respects the pre-selected
    // kind, and the recurring-toggle sync effect elsewhere sees the
    // pre-selected control naturally.
    effect(() => {
      if (!this.isVisible() || this._hasPatchedFromPreselect()) return;
      const kind = this.initialLinkKind();
      const entityId = this.initialEntityId();
      if (!kind) return;
      untracked(() => {
        this.selectedLink.set(kind);
        if (entityId) this.selectedEntityId.set(entityId);
        this.loadEntitiesForKind(kind);
        this._hasPatchedFromPreselect.set(true);
      });
    });
  }

  selectTaskType(type: TaskType): void {
    this.selectedTaskType.set(type);
    this.selectedEntitySubtype.set(null);
    if (TASK_TYPES_REQUIRING_LINK.has(type) && this.selectedLink() === 'none') {
      this.selectedLink.set(null);
      this.selectedEntityId.set(null);
    }
    if (type === TaskType.CREATE) {
      this.selectedEntityId.set(null);
      this.form.controls.needs_approval.setValue(true);
      this.form.controls.needs_approval.disable();
    } else {
      this.form.controls.needs_approval.enable();
      // Reload entities if there's already a selected link kind
      const link = this.selectedLink();
      if (link && link !== 'none') this.loadEntitiesForKind(link);
    }
    this.syncRecurringToggle(this.selectedLink(), this.selectedEntityId());
  }

  selectLink(kind: LinkKind): void {
    if (this.selectedLink() !== kind) {
      this.selectedEntityId.set(null);
      this.selectedEntitySubtype.set(null);
      this.entitySearchQuery.set('');
    }
    this.selectedLink.set(kind);
    if (this.isCreateTask()) {
      if (kind === 'assessment') {
        this.selectedEntitySubtype.set(AssessmentTypeEnum.AI_RISK_ASSESSMENT);
      }
    } else {
      this.loadEntitiesForKind(kind);
    }
    this.syncRecurringToggle(kind, null);
  }

  selectEntity(id: string): void {
    const newId = this.selectedEntityId() === id ? null : id;
    this.selectedEntityId.set(newId);
    this.syncRecurringToggle(this.selectedLink(), newId);
  }

  onEntitySearchValue(value: string): void {
    this.entitySearchQuery.set(value);
  }

  selectEntitySubtype(subtype: string): void {
    this.selectedEntitySubtype.set(this.selectedEntitySubtype() === subtype ? null : subtype);
  }

  onContinue(): void {
    const step = this._currentStep();
    if (step === 'link') {
      this._error.set(null);
      this._errorIsDenial.set(false);
      this.setStep('language');
    } else if (step === 'language') {
      this._error.set(null);
      this._errorIsDenial.set(false);
      this.setStep('details');
    } else if (step === 'details') {
      this._error.set(null);
      this._errorIsDenial.set(false);
      this.setStep('review');
    } else {
      this.submitTask();
    }
  }

  onBack(): void {
    if (this.isTerminalState()) return;
    this._error.set(null);
    this._errorIsDenial.set(false);
    const step = this._currentStep();
    if (step === 'review') {
      this._currentStep.set('details');
    } else if (step === 'details') {
      this._currentStep.set('language');
    } else if (step === 'language') {
      this._currentStep.set('link');
    } else {
      this.cancelled.emit();
    }
  }

  onDismiss(): void {
    if (this.isTerminalState()) return;
    this.cancelled.emit();
  }

  taskTypeLabel(value: TaskType | null): string {
    const fallback = this.transloco.translate('modals.create_task.task_type_label_fallback');
    if (!value) return fallback;
    return this.taskTypes().find(t => t.value === value)?.label ?? fallback;
  }

  linkLabel(value: LinkKind | null): string {
    const fallback = this.transloco.translate('modals.create_task.link_label_fallback');
    if (!value || value === 'none') return fallback;
    return this.linkOptions().find(l => l.value === value)?.label ?? fallback;
  }

  private syncRecurringToggle(link: LinkKind | null, entityId: string | null): void {
    const isRecurringControl =
      this.selectedTaskType() !== TaskType.CREATE &&
      link === 'control' &&
      !!entityId &&
      !!this._controls().find(c => c.control_id === entityId)?.recurrence_unit;

    if (isRecurringControl) {
      this.form.controls.recurring.setValue(true);
      this.form.controls.recurring.enable();
    } else {
      this.form.controls.recurring.setValue(false);
      this.form.controls.recurring.disable();
    }
  }

  private loadEntitiesForKind(kind: LinkKind): void {
    const orgId = this.orgId() ?? this.stateStore.selectedOrgId();
    if (!orgId) return;

    if (kind === 'document' && !this.stateStore.documentsLoading()) {
      this.stateStore.loadDocuments();
    }
    if (kind === 'vendor' && !this.stateStore.vendorsLoading()) {
      this.stateStore.loadVendors();
    }
    if (kind === 'control' && this._controls().length === 0) {
      this._controlsLoading.set(true);
      this.api
        .getControls(orgId)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: controls => {
            this._controls.set(controls);
            this._controlsLoading.set(false);
            this.syncRecurringToggle('control', this.selectedEntityId());
          },
          error: () => this._controlsLoading.set(false),
        });
    }
    if (kind === 'framework' && this._frameworks().length === 0) {
      this._frameworksLoading.set(true);
      this.api
        .getFrameworks(orgId)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: frameworks => {
            this._frameworks.set(frameworks);
            this._frameworksLoading.set(false);
          },
          error: () => this._frameworksLoading.set(false),
        });
    }
    if (kind === 'assessment' && this._assessments().length === 0) {
      this._assessmentsLoading.set(true);
      this.api
        .getAssessments(orgId)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: res => {
            this._assessments.set(res.assessments);
            this._assessmentsLoading.set(false);
          },
          error: () => this._assessmentsLoading.set(false),
        });
    }
  }

  private submitTask(): void {
    const orgId = this.orgId() ?? this.stateStore.selectedOrgId();
    if (!orgId) {
      this._error.set(this.transloco.translate('modals.create_task.error_no_org'));
      return;
    }

    const raw = this.form.getRawValue();
    const name = raw.name?.trim();
    if (!name) {
      this._error.set(this.transloco.translate('modals.create_task.error_name_required'));
      return;
    }

    const kind = this.selectedLink();
    const entityId = this.selectedEntityId();

    const request: TaskCreateRequest = {
      org_id: orgId,
      name,
      task_type: this.selectedTaskType() ?? TaskType.OTHER,
      description: raw.description || null,
      start_date: raw.start_date || null,
      due_date: raw.due_date || null,
      needs_approval: !!raw.needs_approval,
      recurring: !!raw.recurring,
      language: this.contentLanguage(),
    };

    request.assignees = this.selectedAssignees();

    if (this.isCreateTask()) {
      if (kind && kind !== 'none') {
        request.create_entity_category = kind as CreateEntityCategory;
        const subtype = this.selectedEntitySubtype();
        if (subtype) request.create_entity_subtype = subtype;
      }
    } else {
      request.document_id = kind === 'document' ? entityId : null;
      request.control_id = kind === 'control' ? entityId : null;
      request.framework_id = kind === 'framework' ? entityId : null;
      request.vendor_id = kind === 'vendor' ? entityId : null;
      request.assessment_id = kind === 'assessment' ? entityId : null;
    }

    this._isLoading.set(true);
    this._error.set(null);
    this._errorIsDenial.set(false);

    this.taskApi
      .createTask(request)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: task => {
          this._isLoading.set(false);
          this._successMessage.set(this.transloco.translate('modals.create_task.success_created'));
          this._successTimer = setTimeout(() => this.taskCreated.emit(task), 1200);
        },
        error: (err: unknown) => {
          this._isLoading.set(false);
          // Same resolver every denial surface uses — a 403 becomes the
          // localised permission sentence and flips the banner to the
          // amber-lock variant; anything else keeps the failure copy.
          const denial = accessDenialMessage(err, this.transloco);
          this._errorIsDenial.set(denial !== null);
          this._error.set(
            (denial ?? (err as { message?: string } | null)?.message) ||
              this.transloco.translate('modals.create_task.error_create_failed')
          );
        },
      });
  }

  private resetModalState(): void {
    if (this._successTimer) {
      clearTimeout(this._successTimer);
      this._successTimer = null;
    }
    this.form.reset({
      name: '',
      description: '',
      start_date: '',
      due_date: '',
      needs_approval: false,
      recurring: false,
    });
    this.form.controls.recurring.disable();
    this.form.controls.needs_approval.enable();
    this.selectedTaskType.set(null);
    this.selectedLink.set(null);
    this.selectedAssignees.set([]);
    this.selectedEntityId.set(null);
    this.selectedEntitySubtype.set(null);
    this.entitySearchQuery.set('');
    this._controls.set([]);
    this._frameworks.set([]);
    this._assessments.set([]);
    this._controlsLoading.set(false);
    this._frameworksLoading.set(false);
    this._assessmentsLoading.set(false);
    this._error.set(null);
    this._errorIsDenial.set(false);
    this._isLoading.set(false);
    this._successMessage.set(null);
    // Reset the pre-selection guard so a subsequent reopen with fresh
    // initial values re-patches (the pre-select effect only fires when
    // this flag is false).
    this._hasPatchedFromPreselect.set(false);
    this._currentStep.set('link');
    this._maxReachedStep.set('link');
    this.contentLanguage.set(this.stateStore.orgDefaultContentLanguage());
  }
}
